import { Button, DialogContent } from "@mui/material";
import Dialog from "@mui/material/Dialog/Dialog";
import { useTranslation } from "react-i18next";
import {
  useApiErrorTranslation,
  useErrorMessageTranslationBundleQuery,
} from "../hooks/useErrorMessageTranslationBundle";
import { Spinner } from "./spinner";
import { translateErrorMessageText } from "../utils/error";

type ErrorDialogProp = {
  title: string;
  description: string;
  isApiException?: boolean;
  onDismiss: () => void;
};

export default function ExceptionDialog({
  title,
  description,
  onDismiss,
  isApiException = false,
}: ErrorDialogProp) {
  return (
    <Dialog
      open={true}
      maxWidth={"md"}
      onClose={(event, reason) => {
        onDismiss();
      }}
    >
      <ErrorDialogContent
        title={title}
        description={description}
        onDismiss={onDismiss}
        isApiException={isApiException}
      />
    </Dialog>
  );
}

type ErrorDialogContentProp = ErrorDialogProp;

const ErrorDialogContent = ({
  title,
  description,
  onDismiss,
  isApiException = false,
}: ErrorDialogContentProp) => {
  return (
    <div className="mx-[80px] my-[70px]">
      <DialogContent>
        {isApiException ? (
          <ErrorDialogContent2
            title={title}
            description={description}
            onDismiss={onDismiss}
          />
        ) : (
          <Content
            title={title}
            description={description}
            onDismiss={onDismiss}
          />
        )}
      </DialogContent>
    </div>
  );
};

type ErrorDialogContent2Prop = {
  title: string;
  description: string;
  onDismiss: () => void;
};

const ErrorDialogContent2 = ({
  title,
  description,
  onDismiss,
}: ErrorDialogContent2Prop) => {
  const { isLoading } = useErrorMessageTranslationBundleQuery();

  return isLoading ? (
    <DialogContent>
      <div className="flex flex-col items-center justify-center">
        <Spinner size={45} />
      </div>
    </DialogContent>
  ) : (
    <Content title={title} description={description} onDismiss={onDismiss} />
  );
};

type ContentProp = {
  title: string;
  description: string;
  onDismiss: () => void;
};

const Content = ({ title, description, onDismiss }: ContentProp) => {
  const { t } = useTranslation();
  const tErr = useApiErrorTranslation();
  const localizedErrorTitle: string = translateErrorMessageText(title, tErr);
  const localizedErrorDescription: string = translateErrorMessageText(
    description,
    tErr
  );
  return (
    <div className="flex flex-col">
      <div
        style={{ color: "var(--primary-color)" }}
        className="mb-[30px] text-[20px]"
      >
        {localizedErrorTitle}
      </div>
      <div className="mb-[64px] text-[15px] text-[#1A1A1A]">
        {localizedErrorDescription}
      </div>
      <div className="self-end">
        <Button
          type="button"
          variant="contained"
          onClick={onDismiss}
          sx={{
            borderRadius: 10,
          }}
        >
          {t(`Close`)}
        </Button>
      </div>
    </div>
  );
};
